/**
 * InlineFilterCard — WI-43 homepage inline filter card.
 *
 * The single always-visible filter surface that replaces FilterPanel (sidebar),
 * FilterModal (drawer), and FilterBottomSheet. Sits in the page flow between
 * the hero/division strip and the results section.
 *
 * Fields (in DOM order):
 *   1. SPORT — radio grid (3-col desktop, 2-col mobile), single-select
 *   2. POSITION — grouped chips with dashed dividers; placeholder when no sport
 *   3. DIVISION (Optional) — flat chip row, multi-select (OR logic) — WI-43
 *   4. REGION (Optional) — flat chip row, single-select (radio)
 *      └─ STATE sub-chips (WI-38) — appear when a region is selected
 *   5. TARGET ENROLLMENT YEAR — flat chip row, single-select (radio)
 *      Default: next-calendar-year chip is pre-selected on mount.
 *   6. Live count strip footer — placeholder or histogram + count + CTA
 *
 * Position data source (WI-15c): fetchPositionTaxonomy from api/positions.ts.
 *   POSITION_TAXONOMY static constant has been removed from constants/positions.ts.
 *   The pre-fetch on App mount (prefetchAllTaxonomies) ensures the cache is warm
 *   before the user selects a sport in most cases. On cache miss, skeleton chips
 *   appear until the fetch resolves. On fetch error, an inline error + Retry button
 *   are shown; other filter cards remain functional.
 *
 * Sports: exactly 5 (soccer_mens, soccer_womens, football_mens, basketball_mens,
 *   basketball_womens). Lacrosse is absent.
 *
 * Regions: exactly 4 (Northeast, Midwest, South, West) — US Census regions.
 *   The bundle's 6-region list is NOT used.
 *
 * Years: 4 chips computed dynamically as [currentYear, +1, +2, +3].
 */

import React, { useRef, useState, useEffect, useCallback } from 'react';
import { SearchFilters } from '../api/search';
import { fetchPositionTaxonomy, getCachedTaxonomy } from '../api/positions';
import { SportSlug, SportPositionTaxonomy, SPORT_TOOLTIP_COPY } from '../constants/positions';
import { TARGET_YEAR_OPTIONS } from '../constants/years';
import { SkeletonRow } from './ui/SkeletonRow';

// ── Constants ─────────────────────────────────────────────────────────────────

export const SPORTS = [
  { slug: 'soccer_mens',       label: "Men's Soccer",       season: 'Fall' },
  { slug: 'soccer_womens',     label: "Women's Soccer",     season: 'Fall' },
  { slug: 'football_mens',     label: 'Football',           season: 'Fall' },
  { slug: 'basketball_mens',   label: "Men's Basketball",   season: 'Winter' },
  { slug: 'basketball_womens', label: "Women's Basketball", season: 'Winter' },
] as const;

export const DIVISIONS = [
  { slug: 'ncaa_d1', label: 'NCAA D1' },
  { slug: 'ncaa_d2', label: 'NCAA D2' },
  { slug: 'ncaa_d3', label: 'NCAA D3' },
  { slug: 'naia',    label: 'NAIA' },
  { slug: 'njcaa',   label: 'NJCAA' },
] as const;

export const REGIONS = [
  { slug: 'Northeast', label: 'Northeast' },
  { slug: 'Midwest',   label: 'Midwest' },
  { slug: 'South',     label: 'South' },
  { slug: 'West',      label: 'West' },
] as const;

/**
 * US Census region → state codes. Source: FilterPanel.tsx (WI-38).
 * Four regions only — Mid-Atlantic, Southeast, Southwest are NOT present.
 */
export const REGION_STATES: Record<string, { code: string; label: string }[]> = {
  Northeast: [
    { code: 'CT', label: 'Connecticut' }, { code: 'ME', label: 'Maine' },
    { code: 'MA', label: 'Massachusetts' }, { code: 'NH', label: 'New Hampshire' },
    { code: 'NJ', label: 'New Jersey' }, { code: 'NY', label: 'New York' },
    { code: 'PA', label: 'Pennsylvania' }, { code: 'RI', label: 'Rhode Island' },
    { code: 'VT', label: 'Vermont' },
  ],
  Midwest: [
    { code: 'IL', label: 'Illinois' }, { code: 'IN', label: 'Indiana' },
    { code: 'IA', label: 'Iowa' }, { code: 'KS', label: 'Kansas' },
    { code: 'MI', label: 'Michigan' }, { code: 'MN', label: 'Minnesota' },
    { code: 'MO', label: 'Missouri' }, { code: 'NE', label: 'Nebraska' },
    { code: 'ND', label: 'North Dakota' }, { code: 'OH', label: 'Ohio' },
    { code: 'SD', label: 'South Dakota' }, { code: 'WI', label: 'Wisconsin' },
  ],
  South: [
    { code: 'AL', label: 'Alabama' }, { code: 'AR', label: 'Arkansas' },
    { code: 'DE', label: 'Delaware' }, { code: 'DC', label: 'District of Columbia' },
    { code: 'FL', label: 'Florida' }, { code: 'GA', label: 'Georgia' },
    { code: 'KY', label: 'Kentucky' }, { code: 'LA', label: 'Louisiana' },
    { code: 'MD', label: 'Maryland' }, { code: 'MS', label: 'Mississippi' },
    { code: 'NC', label: 'North Carolina' }, { code: 'OK', label: 'Oklahoma' },
    { code: 'SC', label: 'South Carolina' }, { code: 'TN', label: 'Tennessee' },
    { code: 'TX', label: 'Texas' }, { code: 'VA', label: 'Virginia' },
    { code: 'WV', label: 'West Virginia' },
  ],
  West: [
    { code: 'AK', label: 'Alaska' }, { code: 'AZ', label: 'Arizona' },
    { code: 'CA', label: 'California' }, { code: 'CO', label: 'Colorado' },
    { code: 'HI', label: 'Hawaii' }, { code: 'ID', label: 'Idaho' },
    { code: 'MT', label: 'Montana' }, { code: 'NV', label: 'Nevada' },
    { code: 'NM', label: 'New Mexico' }, { code: 'OR', label: 'Oregon' },
    { code: 'UT', label: 'Utah' }, { code: 'WA', label: 'Washington' },
    { code: 'WY', label: 'Wyoming' },
  ],
};

// ── Position group builder ────────────────────────────────────────────────────

interface PositionGroup {
  groupLabel: string;
  groupSlug: string;
  items: { slug: string; label: string; name: string }[];
}

function buildPositionGroups(taxonomy: SportPositionTaxonomy): PositionGroup[] {
  return taxonomy.COARSE_ORDER.map((coarseSlug) => {
    const fineSlugs = taxonomy.FINE_UNDER_COARSE[coarseSlug] ?? [];
    const chipSlugs = fineSlugs.length > 0 ? fineSlugs : [coarseSlug];
    return {
      groupSlug: coarseSlug,
      groupLabel: taxonomy.POSITION_FULL_NAMES[coarseSlug] ?? coarseSlug,
      items: chipSlugs.map((slug) => ({
        slug,
        label: taxonomy.POSITION_DISPLAY_NAMES[slug] ?? slug.toUpperCase(),
        name: taxonomy.POSITION_FULL_NAMES[slug] ?? slug,
      })),
    };
  });
}

// ── Position taxonomy fetch state ─────────────────────────────────────────────

type TaxonomyStatus = 'idle' | 'loading' | 'ready' | 'error';

interface TaxonomyState {
  status: TaxonomyStatus;
  taxonomy: SportPositionTaxonomy | null;
  error: string | null;
}

// ── StateChipSection ──────────────────────────────────────────────────────────

interface StateChipSectionProps {
  region: string;
  selectedStates: string[] | undefined;
  onChange: (states: string[] | undefined) => void;
}

function StateChipSection({ region, selectedStates, onChange }: StateChipSectionProps) {
  const [expanded, setExpanded] = React.useState<boolean>(
    (selectedStates?.length ?? 0) > 0
  );
  const states = REGION_STATES[region] ?? [];
  const showToggle = states.length > 9;

  function handleToggle(code: string) {
    const current = selectedStates ?? [];
    const next = current.includes(code)
      ? current.filter((c) => c !== code)
      : [...current, code];
    onChange(next.length > 0 ? next : undefined);
  }

  return (
    <div className="ors-state-sub">
      <div className="ors-state-sub-label">
        <span>State</span>
        <span className="ors-field-hint">
          {selectedStates && selectedStates.length > 0
            ? `${selectedStates.length} of ${states.length} selected`
            : `All ${states.length} states`}
        </span>
      </div>
      <div
        className="ors-chip-row"
        style={
          !expanded && showToggle
            ? { maxHeight: 52, overflow: 'hidden' }
            : undefined
        }
      >
        {states.map(({ code, label }) => {
          const isSelected = selectedStates?.includes(code) ?? false;
          return (
            <button
              key={code}
              type="button"
              role="checkbox"
              aria-checked={isSelected}
              aria-label={label}
              title={label}
              onClick={() => handleToggle(code)}
              className="ors-chip"
              style={{ fontFamily: 'var(--ors-font-mono)', fontSize: 12, padding: '5px 9px' }}
            >
              {code}
            </button>
          );
        })}
        {(selectedStates?.length ?? 0) > 0 && (
          <button
            type="button"
            className="ors-chip-clear"
            onClick={() => onChange(undefined)}
          >
            Clear states
          </button>
        )}
      </div>
      {showToggle && (
        <button
          type="button"
          aria-expanded={expanded}
          onClick={() => setExpanded((v) => !v)}
          className="mt-1 w-full text-right text-[11px] focus:outline-none focus-visible:underline"
          style={{ color: 'var(--ors-accent)', minHeight: 32, display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}
        >
          {expanded ? 'Show fewer states' : `+ Show all ${states.length} states`}
        </button>
      )}
    </div>
  );
}

// ── Live Count Strip ──────────────────────────────────────────────────────────

interface HistBucket {
  range: string;
  count: number;
}

interface LiveCountStripProps {
  visible: boolean;
  loading: boolean;
  count: number;
  buckets: HistBucket[];
  canSubmit: boolean;
  onSubmit: () => void;
  hasResults: boolean;
}

function SimpleHistogram({ buckets, dim }: { buckets: HistBucket[]; dim: boolean }) {
  const max = Math.max(1, ...buckets.map((b) => b.count));
  return (
    <div className={`ors-hist${dim ? ' is-dim' : ''}`} aria-hidden="true">
      {buckets.map((b, i) => (
        <div key={i} className="ors-hist-col" title={`${b.range}: ${b.count} schools`}>
          <div className="ors-hist-bar" style={{ height: `${(b.count / max) * 100}%` }} />
          <div className="ors-hist-x">{b.range}</div>
        </div>
      ))}
    </div>
  );
}

function LiveCountStrip({ visible, loading, count, buckets, canSubmit, onSubmit, hasResults }: LiveCountStripProps) {
  if (!visible) {
    return (
      <div className="ors-strip is-placeholder">
        <p className="ors-strip-placeholder-text">
          Select a sport and position to see live counts.
        </p>
        <button
          type="button"
          className="ors-btn-primary"
          onClick={onSubmit}
          disabled={!canSubmit}
          aria-disabled={!canSubmit}
        >
          {hasResults ? 'Update results' : 'Show schools'}
          <span aria-hidden="true"> →</span>
        </button>
      </div>
    );
  }

  return (
    <div className="ors-strip">
      <div className="ors-strip-hist">
        <div className="ors-strip-label">
          <span className="ors-strip-eyebrow">Fit-score distribution</span>
          <span
            className="ors-strip-count"
            aria-live={loading ? 'off' : 'polite'}
            aria-atomic="true"
          >
            {loading
              ? '…'
              : <><b>{count}</b> {count === 1 ? 'school matches' : 'schools match'}</>}
          </span>
        </div>
        <SimpleHistogram buckets={buckets} dim={loading} />
      </div>
      <div className="ors-strip-cta">
        <button
          type="button"
          className="ors-btn-primary"
          onClick={onSubmit}
          disabled={!canSubmit}
          aria-disabled={!canSubmit}
        >
          {hasResults ? 'Update results' : 'Show schools'}
          <span aria-hidden="true"> →</span>
        </button>
      </div>
    </div>
  );
}

// ── InlineFilterCard ──────────────────────────────────────────────────────────

interface InlineFilterCardProps {
  filters: SearchFilters;
  onFiltersChange: (filters: SearchFilters) => void;
  onSubmit: () => void;
  /** Ref to the first sport radio button (for keyboard focus on "Edit filters"). */
  firstRadioRef?: React.RefObject<HTMLButtonElement | null>;
  /** Live count strip props */
  histLoading: boolean;
  histCount: number;
  histBuckets: HistBucket[];
  hasResults: boolean;
}

export function InlineFilterCard({
  filters,
  onFiltersChange,
  onSubmit,
  firstRadioRef,
  histLoading,
  histCount,
  histBuckets,
  hasResults,
}: InlineFilterCardProps) {
  // ── Position taxonomy async state ───────────────────────────────────────────
  // Track {status, taxonomy, error} per the current selected sport.
  // On sport change: check cache first (sync), then fetch if missing.
  const [taxonomyState, setTaxonomyState] = useState<TaxonomyState>(() => {
    if (!filters.sport) return { status: 'idle', taxonomy: null, error: null };
    const cached = getCachedTaxonomy(filters.sport as SportSlug);
    if (cached) return { status: 'ready', taxonomy: cached, error: null };
    return { status: 'idle', taxonomy: null, error: null };
  });

  // Fetch taxonomy for the current sport when sport changes.
  const loadTaxonomy = useCallback(async (sport: SportSlug) => {
    // Sync cache hit — no state update needed for status, just ensure ready.
    const cached = getCachedTaxonomy(sport);
    if (cached) {
      setTaxonomyState({ status: 'ready', taxonomy: cached, error: null });
      return;
    }
    // Cache miss: show loading skeleton, fetch, then update state.
    setTaxonomyState({ status: 'loading', taxonomy: null, error: null });
    try {
      const taxonomy = await fetchPositionTaxonomy(sport);
      setTaxonomyState({ status: 'ready', taxonomy, error: null });
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to load positions';
      setTaxonomyState({ status: 'error', taxonomy: null, error: message });
    }
  }, []);

  // Re-fetch when sport changes.
  useEffect(() => {
    if (!filters.sport) {
      setTaxonomyState({ status: 'idle', taxonomy: null, error: null });
      return;
    }
    loadTaxonomy(filters.sport as SportSlug);
  }, [filters.sport, loadTaxonomy]);

  // Build position groups from taxonomy (merge in tooltip copy from frontend constants).
  const positionGroups: PositionGroup[] = (() => {
    if (taxonomyState.status !== 'ready' || !taxonomyState.taxonomy) return [];
    return buildPositionGroups(taxonomyState.taxonomy);
  })();

  const hasPreviewContext = Boolean(
    filters.sport && filters.position && filters.position.length > 0
  );

  const canSubmit = Boolean(filters.sport && filters.target_year);

  // ── Filter change helpers ─────────────────────────────────────────────────

  const handleSportChange = useCallback((slug: string) => {
    if (slug === filters.sport) return;
    onFiltersChange({ ...filters, sport: slug, position: undefined });
  }, [filters, onFiltersChange]);

  const togglePosition = useCallback((slug: string) => {
    const current = filters.position ?? [];
    const next = current.includes(slug)
      ? current.filter((p) => p !== slug)
      : [...current, slug];
    onFiltersChange({ ...filters, position: next.length > 0 ? next : undefined });
  }, [filters, onFiltersChange]);

  const selectGroupAll = useCallback((items: { slug: string }[]) => {
    const ids = items.map((p) => p.slug);
    const current = filters.position ?? [];
    onFiltersChange({ ...filters, position: Array.from(new Set([...current, ...ids])) });
  }, [filters, onFiltersChange]);

  const selectGroupNone = useCallback((items: { slug: string }[]) => {
    const ids = new Set(items.map((p) => p.slug));
    const current = filters.position ?? [];
    onFiltersChange({ ...filters, position: current.filter((p) => !ids.has(p)) });
  }, [filters, onFiltersChange]);

  const toggleDivision = useCallback((slug: string) => {
    const current = filters.division ?? [];
    const next = current.includes(slug)
      ? current.filter((d) => d !== slug)
      : [...current, slug];
    onFiltersChange({ ...filters, division: next.length > 0 ? next : undefined });
  }, [filters, onFiltersChange]);

  const toggleRegion = useCallback((slug: string) => {
    const isRemoving = filters.region === slug;
    if (isRemoving) {
      onFiltersChange({ ...filters, region: undefined, state: undefined });
    } else {
      onFiltersChange({ ...filters, region: slug, state: undefined });
    }
  }, [filters, onFiltersChange]);

  const handleStateChange = useCallback((states: string[] | undefined) => {
    onFiltersChange({ ...filters, state: states });
  }, [filters, onFiltersChange]);

  const handleYearChange = useCallback((year: number) => {
    onFiltersChange({ ...filters, target_year: year });
  }, [filters, onFiltersChange]);

  // Retry handler: clear cached error, re-fetch.
  const handlePositionRetry = useCallback(() => {
    if (!filters.sport) return;
    loadTaxonomy(filters.sport as SportSlug);
  }, [filters.sport, loadTaxonomy]);

  return (
    <section id="filter-form" className="ors-form-shell" style={{ marginBottom: 0 }}>
      {/* Form body */}
      <div className="ors-form-body">
        <div className="ors-form-head">
          <h2 className="ors-h2">Filter open positions</h2>
          <p className="ors-form-help">
            Pick a sport to start. Position groups, live counts, and state filters appear as you go.
          </p>
        </div>

        {/* ── SPORT ─────────────────────────────────────────────────── */}
        <div className="ors-field" role="radiogroup" aria-label="Sport">
          <div className="ors-field-label">Sport</div>
          <div className="ors-radio-grid">
            {SPORTS.map((s, idx) => {
              const isSelected = filters.sport === s.slug;
              return (
                <button
                  key={s.slug}
                  type="button"
                  role="radio"
                  aria-checked={isSelected}
                  ref={idx === 0 ? (firstRadioRef as React.RefObject<HTMLButtonElement>) : undefined}
                  onClick={() => handleSportChange(s.slug)}
                  className="ors-radio"
                >
                  <span className="ors-radio-dot" aria-hidden="true"><span /></span>
                  <span className="ors-radio-body">
                    <span className="ors-radio-label">{s.label}</span>
                    <span className="ors-radio-sub">{s.season}</span>
                  </span>
                </button>
              );
            })}
          </div>
        </div>

        {/* ── POSITION ───────────────────────────────────────────────── */}
        <div className="ors-field">
          <div className="ors-field-label">
            Position
            <span className="ors-field-hint">
              {filters.sport
                ? 'Click a group label to toggle all.'
                : 'Select a sport first'}
            </span>
          </div>

          {/* No sport selected */}
          {!filters.sport ? (
            <div className="ors-pos-stack">
              <div className="ors-pos-group" style={{ gridTemplateColumns: '1fr' }}>
                <p className="ors-empty-positions">
                  Position options appear after you pick a sport.
                </p>
              </div>
            </div>
          ) : taxonomyState.status === 'loading' ? (
            /* Loading: skeleton chip placeholders — sport chip selection is NOT blocked */
            <div
              className="ors-pos-stack"
              aria-busy="true"
              aria-label="Loading position options"
              role="group"
            >
              <SkeletonRow height="h-8" className="w-full rounded" />
              <SkeletonRow height="h-8" className="w-full rounded" />
              <SkeletonRow height="h-8" className="w-3/4 rounded" />
            </div>
          ) : taxonomyState.status === 'error' ? (
            /* Error: scoped to position card; other filter cards remain functional */
            <div
              className="ors-pos-stack"
              role="alert"
              aria-label="Position options failed to load"
            >
              <p className="ors-empty-positions" style={{ color: 'var(--ors-error, #dc2626)' }}>
                Failed to load positions. Please try again.
              </p>
              <button
                type="button"
                className="ors-btn-primary"
                style={{ marginTop: 8, minHeight: 44 }}
                onClick={handlePositionRetry}
                aria-label="Retry loading position options"
              >
                Retry
              </button>
            </div>
          ) : (
            /* Ready: render position chip groups */
            <div className="ors-pos-stack" role="group" aria-label="Position">
              {positionGroups.map((group) => {
                const selectedInGroup = group.items.filter(
                  (item) => (filters.position ?? []).includes(item.slug)
                );
                const allSelected = selectedInGroup.length === group.items.length;
                const noneSelected = selectedInGroup.length === 0;
                return (
                  <div key={group.groupSlug} className="ors-pos-group">
                    <button
                      type="button"
                      className={`ors-pos-group-label${!noneSelected ? ' is-active' : ''}`}
                      onClick={() =>
                        allSelected
                          ? selectGroupNone(group.items)
                          : selectGroupAll(group.items)
                      }
                      title={
                        allSelected
                          ? `Clear ${group.groupLabel}`
                          : `Select all ${group.groupLabel}`
                      }
                    >
                      <span>{group.groupLabel}</span>
                      {!noneSelected && (
                        <span className="ors-pos-group-count">
                          {selectedInGroup.length}/{group.items.length}
                        </span>
                      )}
                    </button>
                    <div className="ors-chip-row">
                      {group.items.map((item) => {
                        const isSelected = (filters.position ?? []).includes(item.slug);
                        return (
                          <button
                            key={item.slug}
                            type="button"
                            role="checkbox"
                            aria-checked={isSelected}
                            title={item.name}
                            onClick={() => togglePosition(item.slug)}
                            className="ors-chip"
                          >
                            {item.label}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* ── DIVISION (Optional) ─────────────────────────────────────── */}
        <div className="ors-field">
          <div className="ors-field-label">
            Division
            <span className="ors-field-hint">Optional · multi-select</span>
          </div>
          <div className="ors-chip-row" role="group" aria-label="Division">
            {DIVISIONS.map((d) => {
              const isSelected = (filters.division ?? []).includes(d.slug);
              return (
                <button
                  key={d.slug}
                  type="button"
                  role="checkbox"
                  aria-checked={isSelected}
                  onClick={() => toggleDivision(d.slug)}
                  className="ors-chip"
                >
                  {d.label}
                </button>
              );
            })}
          </div>
        </div>

        {/* ── REGION (Optional) ───────────────────────────────────────── */}
        <div className="ors-field">
          <div className="ors-field-label">
            Region
            <span className="ors-field-hint">Optional</span>
          </div>
          <div className="ors-chip-row" role="radiogroup" aria-label="Region">
            {REGIONS.map((r) => {
              const isSelected = filters.region === r.slug;
              return (
                <button
                  key={r.slug}
                  type="button"
                  role="radio"
                  aria-checked={isSelected}
                  onClick={() => toggleRegion(r.slug)}
                  className="ors-chip"
                >
                  {r.label}
                </button>
              );
            })}
          </div>

          {/* WI-38: state sub-chips */}
          {filters.region && (
            <StateChipSection
              region={filters.region}
              selectedStates={filters.state}
              onChange={handleStateChange}
            />
          )}
        </div>

        {/* ── TARGET ENROLLMENT YEAR ──────────────────────────────────── */}
        <div className="ors-field">
          <div className="ors-field-label">Target enrollment year</div>
          <div className="ors-chip-row" role="radiogroup" aria-label="Target enrollment year">
            {TARGET_YEAR_OPTIONS.map((y) => {
              const isSelected = filters.target_year === y;
              return (
                <button
                  key={y}
                  type="button"
                  role="radio"
                  aria-checked={isSelected}
                  onClick={() => handleYearChange(y)}
                  className="ors-chip"
                  style={{ fontVariantNumeric: 'tabular-nums' }}
                >
                  {y}
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* ── Live count strip (flush to bottom of card) ──────────────── */}
      <LiveCountStrip
        visible={hasPreviewContext}
        loading={histLoading}
        count={histCount}
        buckets={histBuckets}
        canSubmit={canSubmit}
        onSubmit={onSubmit}
        hasResults={hasResults}
      />
    </section>
  );
}

// ── StickyFilterBar ───────────────────────────────────────────────────────────

interface StickyFilterBarProps {
  visible: boolean;
  filters: SearchFilters;
  resultCount: number;
  onEdit: () => void;
}

function buildFilterSummary(filters: SearchFilters): string {
  const parts: string[] = [];

  const sport = SPORTS.find((s) => s.slug === filters.sport);
  if (sport) parts.push(sport.label);

  if (filters.position && filters.position.length > 0) {
    const pos = filters.position;
    if (pos.length === 1) {
      parts.push(pos[0]!.toUpperCase());
    } else if (pos.length === 2) {
      parts.push(`${pos[0]!.toUpperCase()}, ${pos[1]!.toUpperCase()}`);
    } else {
      parts.push(`${pos[0]!.toUpperCase()} +${pos.length - 1}`);
    }
  }

  if (filters.division && filters.division.length > 0) {
    const divLabels = filters.division.map(
      (d) => DIVISIONS.find((x) => x.slug === d)?.label ?? d
    );
    if (divLabels.length === 1) {
      parts.push(divLabels[0]!);
    } else {
      parts.push(`${divLabels[0]!} +${divLabels.length - 1}`);
    }
  }

  if (filters.state && filters.state.length > 0) {
    const states = filters.state;
    if (states.length === 1) parts.push(states[0]!);
    else parts.push(`${states[0]!} +${states.length - 1}`);
  } else if (filters.region) {
    parts.push(filters.region);
  }

  if (filters.target_year) parts.push(String(filters.target_year));

  return parts.join(' · ');
}

export function StickyFilterBar({ visible, filters, resultCount, onEdit }: StickyFilterBarProps) {
  const summary = buildFilterSummary(filters);
  return (
    <div
      className={`ors-sticky${visible ? ' is-visible' : ''}`}
      aria-hidden={!visible}
    >
      <div className="ors-sticky-inner">
        <div className="ors-summary-row">
          <div className="ors-summary-text">
            <span className="ors-summary-eyebrow">Filtering for</span>
            <span className="ors-summary-label">{summary || 'No filters set'}</span>
          </div>
          <div className="ors-summary-count">
            <b>{resultCount}</b> <span>schools</span>
          </div>
        </div>
        <button
          type="button"
          className="ors-btn-primary"
          style={{ padding: '8px 14px', fontSize: 13.5 }}
          onClick={onEdit}
          aria-label="Edit search filters"
        >
          Edit filters
        </button>
      </div>
    </div>
  );
}

// ── DivisionStrip ─────────────────────────────────────────────────────────────

interface DivisionStripDivision {
  slug: string;
  label: string;
  count: number;
}

interface DivisionStripProps {
  divisions: DivisionStripDivision[];
  onPillClick: (divisionSlug: string) => void;
}

export function DivisionStrip({ divisions, onPillClick }: DivisionStripProps) {
  return (
    <section className="ors-strip-browse" aria-label="Browse by division">
      <div className="ors-strip-browse-label">
        <span className="ors-eyebrow" style={{ margin: 0 }}>Browse by division</span>
        <span className="ors-strip-browse-hint">Skip the filter — see every school</span>
      </div>
      <div className="ors-strip-browse-pills">
        {divisions.map((d) => (
          <button
            key={d.slug}
            type="button"
            className="ors-div-pill"
            data-div={d.slug}
            onClick={() => onPillClick(d.slug)}
          >
            <span className="ors-div-pill-name">{d.label}</span>
            <span className="ors-div-pill-count">{d.count > 0 ? d.count.toLocaleString() : '—'}</span>
            <span className="ors-div-pill-arrow" aria-hidden="true">→</span>
          </button>
        ))}
      </div>
    </section>
  );
}
